import { useMemo, useState } from 'react'
import { useCRUD, useFetch, useFetchUncachedCb } from '@gluedigital/ruse-fetch-extras'
import { useDispatch, useSelector } from 'react-redux'
import { login, logout } from 'src/store/actions/user'
import {
  AssetActivity, Asset, AssetDetails, AssetWithAccess,
  User, UserWithAccess, UserActivity, Client, TokenValidate, LoginRequest, UserLogin, UserLoginWithSecret,
  UserIsChild
} from 'src/types'
import { filterAssets, filterUsers } from 'src/util/filters'
const base = process.env.API_URL || 'https://ada-api.securitas.es/api'

interface ContactBody {
  description: string
  issueType?: string
  assetId?: string
}

export const useUserActions = () => {
  const doFetch = useFetchUncachedCb()
  const dispatch = useDispatch()
  return {
    login: async ({ associationRequest, code, email, password }: LoginRequest): Promise<UserLogin> => {
      const payload = await doFetch<UserLogin>(base + '/auth/login', {
        body: {
          email,
          password,
          code,
          associationRequest,
          base: 'client'
        }
      })
      if (payload.role !== 'CLIENT' && payload.role !== 'SEMIADMIN') throw new Error('Wrong role')
      dispatch(login(payload))
      return payload
    },
    associate: async (secret: string, id: string): Promise<UserLoginWithSecret> => {
      const payload = await doFetch<UserLoginWithSecret>(`${base}/auth/validate/mfa`, {
        body: {
          secret,
          id
        }
      })
      const { secret: secretResponse, ...user } = payload
      if (payload.role !== 'CLIENT' && payload.role !== 'SEMIADMIN') throw new Error('Wrong role')
      dispatch(login(user))
      return payload
    },
    recoveryStart: (email: string) =>
      doFetch(base + '/auth/recovery', { body: { email } }),
    recoveryComplete: (user: string, token: string, password: string) =>
      doFetch(base + '/auth/recovery/complete', { body: { user, token, password } }),
    contact: (body: ContactBody) =>
      doFetch(base + '/client/contact', { body }),
    logout: () => dispatch(logout())
  }
}

export const useAssets = (): Asset[] =>
  useFetch(base + '/client/actives')

export const useHiddenAssets = (): Asset[] =>
  useFetch(base + '/client/actives/hidden')

export const useUsers = (): User[] =>
  useFetch(base + '/client/users')

export const useUser = (id: number): UserIsChild =>
  useFetch(id && (base + '/client/user/' + id))

export const useUserAssets = (id: number): AssetWithAccess[] =>
  useFetch(base + '/client/actives/access/' + (id || 0))

export const useUserActivity = (id: number): UserActivity[] =>
  useFetch(id && (base + '/client/user/' + id + '/active/activity'))

export const useAsset = (slug: string): AssetDetails =>
  useFetch(base + '/client/active/' + slug)

export const useAssetUsers = (slug: string): UserWithAccess[] =>
  useFetch(base + '/client/actives/' + slug + '/users/access')

export const useAssetActivity = (slug: string): AssetActivity[] =>
  useFetch(base + '/client/active/' + slug + '/activity')

export const useClient = (): Client =>
  useFetch(base + '/client/me')

export const useConfirmToken = (user: string, token: string): TokenValidate =>
  useFetch(base + '/auth/recovery/checkIsValidToken', { body: { user, token } })

export const useExportReport = (): any => {
  const today = new Date().toISOString().slice(0, 10)
  const fileName = today + '_Securitas_Client_Report.zip'
  const token = useSelector((s:any) => s.user.token)
  return async () => {
    const data = await fetch(base + '/client/export', {
      headers: { Authorization: 'Bearer ' + token }
    })
    const blob = await data.blob()
    const link = document.createElement('a')
    link.setAttribute('href', window.URL.createObjectURL(blob))
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
  }
}

// Filtered views

export const useFilteredAssets = (filter: string, location?: number[]): Asset[] => {
  const assets = useAssets()
  return useMemo(() => assets.filter(filterAssets(filter, location)), [assets, filter, location])
}

export const useFilteredUsers = (filter: string): User[] => {
  const users = useUsers()
  return useMemo(() => users.filter(filterUsers(filter)), [users, filter])
}

// CRUD

export const useClientCRUD = () => useCRUD<string, Client>(base + '/client')
export const useUserCRUD = () => useCRUD<string, User>(base + '/client/user')
export const useAssetCRUD = () => useCRUD<string, AssetDetails>(base + '/client/active')

// File Upload

interface CSVUploadRequest {
  fileActives?: File
  fileUser?: File
  fileUserActives?: File
}

interface CSVUploadResponse {
  usersUpdated: any
  activesUpdated: any
  permissionsAssigned: any
}

type UploadStatus = 'idle' | 'sending' | 'error' | 'success'

export type UploadType = 'partial' | 'complete'

export const useUploadFiles = () => {
  const fetch = useFetchUncachedCb()
  const [status, setStatus] = useState<UploadStatus>('idle')

  return {
    send: async (files: CSVUploadRequest, type: UploadType): Promise<void> => {
      setStatus('sending')
      const body = new FormData()
      for (const key in files) {
        const file = files[key]
        body.append(key, file)
      }
      body.append('total', type === 'complete' ? 'true' : 'false')
      try {
        const res = await fetch<CSVUploadResponse>(base + '/client/upload', { body })
        const someFile = Object.values(res).some(file => file !== null && file !== undefined)
        setStatus(someFile ? 'success' : 'error')
      } catch (err) {
        setStatus('error')
      }
    },
    status
  }
}
